import React from 'react';
import Toggle from 'Components/DesignSystem/Toggle/Toggle';
import Radio from 'Components/DesignSystem/Radio/Radio';
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import Typography from 'Components/DesignSystem/Typography/Typography';

/**
 * The Restrictions control, shared by Field Details and Category Details.
 *
 * **Why this exists.** The two panels carried a ~65-line copy of this block each.
 * After normalising the `id` prefixes the two were identical apart from three
 * lines — one of which had already drifted: the section heading rendered
 * UPPERCASE in the field panel and sentence case in the category panel. Same
 * control, two renderings, shipped.
 *
 * **What it does NOT own.** Every derivation and every write stays with the
 * caller, because the two panels store this state under different keys
 * (`dataConfiguration.fieldRequired` vs `data.required`) and write it through
 * different mechanisms (a `dataConfiguration` patch vs `useCategoryUpdate`
 * field-by-field). This component takes derived booleans in and sends intents
 * out. Nothing about the state machine moved here, so nothing about it can drift
 * between the two callers as a result of this extraction.
 *
 * **Accessibility.** The options were `<div role="button" onClick>` with no
 * `tabIndex` and no key handler, so Required could not be set by keyboard at all.
 * They are real radios sharing a `name` now, which is what gives the group its
 * single tab stop and arrow-key navigation.
 */

export type RestrictionsMode = 'recommended' | 'required' | null;

export type RestrictionsToggleProps = {
  /** Prefix for this instance's DOM ids and test ids. */
  baseId: string;
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  className?: string;
};

export type RestrictionsOptionsProps = {
  /** Prefix for this instance's DOM ids and test ids. */
  baseId: string;
  /** Whether the options are shown at all — the caller's `restrictionsEnabled`. */
  enabled: boolean;
  mode: RestrictionsMode;
  onSelectRecommended: () => void;
  onSelectRequired: () => void;
  blockAddToCart: boolean;
  onBlockAddToCartChange: (checked: boolean) => void;
  lockNavigation: boolean;
  onLockNavigationChange: (checked: boolean) => void;
  className?: string;
};

const RECOMMENDED_DESCRIPTION =
  'Shows a * indicator on this field and its page tab. Soft guidance only - no hard gates.';
const REQUIRED_DESCRIPTION =
  'Show a * indicator and enforce fulfilment. Choose at least one enforcement below';
const BLOCK_ADD_TO_CART_DESCRIPTION =
  'Disables Add to Cart until fulfilled. Hover: "Add to cart disabled until Required fields/products are selected."';
const LOCK_NAVIGATION_DESCRIPTION =
  'Disables Next button and all page tabs until fulfilled. Toast: "Please select required items before proceeding."';

/**
 * A control label that carries its own explanation.
 *
 * Both lines sit INSIDE the `<label>` deliberately. Moving the description to a
 * sibling with `aria-describedby` is the tidier ARIA pattern, but it takes the
 * description out of the click target — and clicking the explanation to choose
 * the option it explains is behaviour these panels already had.
 */
const LabelWithDescription = ({ title, description }: { title: string; description: string }) => (
  <span className="tw-block">
    <Typography variant="body-primary" className="tw-font-medium tw-text-ink tw-block">
      {title}
    </Typography>
    <Typography variant="caption-metadata" className="tw-text-ink-secondary tw-block">
      {description}
    </Typography>
  </span>
);

/**
 * The switch that reveals the options. Kept separate from them because the two
 * sit in different parts of the panel — the switch rides the header row beside
 * the Title field, the options sit in a band below it.
 */
export const RestrictionsToggle = ({
  baseId,
  enabled,
  onEnabledChange,
  className = '',
}: RestrictionsToggleProps) => (
  <Toggle
    id={`${baseId}-restrictions-toggle`}
    data-testid={`${baseId}-restrictions-toggle`}
    label="Restrictions"
    labelPlacement="top"
    checked={enabled}
    onChange={onEnabledChange}
    className={className}
  />
);

export const RestrictionsOptions = ({
  baseId,
  enabled,
  mode,
  onSelectRecommended,
  onSelectRequired,
  blockAddToCart,
  onBlockAddToCartChange,
  lockNavigation,
  onLockNavigationChange,
  className = '',
}: RestrictionsOptionsProps) => {
  if (!enabled) return null;

  return (
    <div
      id={`${baseId}-restrictions-options`}
      data-testid={`${baseId}-restrictions-options`}
      className={`tw-flex tw-flex-col tw-gap-3 ${className}`}
    >
      <Typography
        variant="sub-title-label"
        as="p"
        id={`${baseId}-restrictions-label`}
        className="tw-text-ink-secondary"
      >
        Restrictions
      </Typography>
      {/* One `name` across both options is what buys the group its single tab
          stop and arrow-key navigation — the browser gives that only to radios
          that share a name. */}
      <div
        role="radiogroup"
        aria-labelledby={`${baseId}-restrictions-label`}
        className="tw-flex tw-flex-col tw-gap-3"
      >
        <Radio
          id={`${baseId}-recommended-option`}
          data-testid={`${baseId}-restrictions-recommended`}
          name={`${baseId}-restrictions`}
          value="recommended"
          checked={mode === 'recommended'}
          onChange={onSelectRecommended}
          /*
            Re-fire when the option is ALREADY selected — see the note on the
            Required radio below.
          */
          onClick={() => { if (mode === 'recommended') onSelectRecommended(); }}
          label={
            <LabelWithDescription title="Recommended" description={RECOMMENDED_DESCRIPTION} />
          }
        />
        <Radio
          id={`${baseId}-required-option`}
          data-testid={`${baseId}-restrictions-required`}
          name={`${baseId}-restrictions`}
          value="required"
          checked={mode === 'required'}
          onChange={onSelectRequired}
          /*
            A radio fires `change` only when the selection actually CHANGES, so
            clicking the option that is already on is a no-op — correct radio
            behaviour, and a behaviour change from the `div role="button"` these
            replaced, which re-ran the handler on every click.
            That mattered here. Both panels derive "Recommended" from a legacy
            shape (`fieldRequired: true` with no `recommended` key), so a field
            saved by an older build DISPLAYS as Recommended while still storing the
            old shape; clicking Recommended used to rewrite it to the current one.
            `onClick` restores exactly that, and only that — it is guarded on
            `checked`, so the unchecked path still fires once, through `change`.
          */
          onClick={() => { if (mode === 'required') onSelectRequired(); }}
          label={<LabelWithDescription title="Required" description={REQUIRED_DESCRIPTION} />}
        />
        {/*
          The enforcements are a SIBLING of the Required radio, not a child of it.
          They used to be nested inside the option's markup, which worked only
          because that option was a `div` — a real `<label>` cannot contain another
          interactive control without clicks on the inner one also reaching the
          outer. `ml-8` = the 20px control plus the 12px gap, so they line up under
          the label text rather than under the radio.
        */}
        {mode === 'required' && (
          <div
            id={`${baseId}-sub-controls`}
            data-testid={`${baseId}-restrictions-enforcements`}
            className="tw-ml-8 tw-flex tw-flex-col tw-gap-2"
          >
            <Checkbox
              id={`${baseId}-block-add-to-cart-checkbox`}
              data-testid={`${baseId}-restrictions-block-add-to-cart`}
              checked={blockAddToCart}
              onChange={onBlockAddToCartChange}
              labelClamp={false}
              label={
                <LabelWithDescription
                  title="Block Add to Cart"
                  description={BLOCK_ADD_TO_CART_DESCRIPTION}
                />
              }
            />
            <Checkbox
              id={`${baseId}-lock-navigation-checkbox`}
              data-testid={`${baseId}-restrictions-lock-navigation`}
              checked={lockNavigation}
              onChange={onLockNavigationChange}
              labelClamp={false}
              label={
                <LabelWithDescription
                  title="Lock Navigation"
                  description={LOCK_NAVIGATION_DESCRIPTION}
                />
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};
